import { formatNumber } from "util/formatNumber";

/** Whole kilograms for display — converted totals carry noisy decimals. */
export const formatKg = (kg: number) => `${formatNumber(Math.round(kg))} kg`;

/**
 * The subset of `FacilityAccumulationSummary` the on-site accumulation card's
 * caption and judged-math derivation reads. A structural (not imported) type
 * so this module has no dependency on the generated query shape.
 */
export type OnSiteCapSummaryInput = {
  nonAcuteOnSiteKg: number;
  acuteOnSiteKg: number;
  onSiteCapKg?: number | null;
  acuteOnSiteCapKg?: number | null;
  onSiteCapAppliesTo?: string | null;
  episodicExemptNonAcuteKg: number;
  episodicExemptAcuteKg: number;
  /** The generator status the threshold was judged against, in the state's own words; null = unresolved. */
  accumulationThresholdJudgedAgainst?: { abbreviation: string } | null;
};

export type OnSiteCapDisplay = {
  /** The card's headline caption: the plain "accumulated on-site" variants, or
   * the judged-math sentence once an episodic exemption applies. */
  caption: string;
  // Only present when governing rules impose a threshold on acute waste specifically
  acuteCaption?: string;
  acuteOver: boolean;
  /** Exemption sub-line, present only when exempt mass is nonzero. */
  exemptCaption?: string;
};

/**
 * Derives the on-site accumulation stat card's caption and sub-caption text,
 * folding in the episodic exemption once an active event carves mass out of
 * the threshold judgment. Zero exempt mass (the common case: no active event,
 * or none of its containers count against the threshold) reproduces today's
 * plain totals so the card doesn't change for facilities with no episodic
 * activity.
 */
export function deriveOnSiteCapDisplay(
  summary: OnSiteCapSummaryInput | undefined
): OnSiteCapDisplay {
  const totalOnSiteKg = (summary?.nonAcuteOnSiteKg ?? 0) + (summary?.acuteOnSiteKg ?? 0);
  const exemptKg = (summary?.episodicExemptNonAcuteKg ?? 0) + (summary?.episodicExemptAcuteKg ?? 0);

  // Judged net of exempt acute mass, mirroring the backend's
  // `judgedAcuteKg`: an episodic event's exempt acute container is still
  // physically on site (counted in `acuteOnSiteKg` above) but doesn't count
  // against the 1 kg acute threshold, so the sub-line must agree with the
  // over/not-over verdict the summary's `overCap` already reflects.
  const acuteExemptKg = summary?.episodicExemptAcuteKg ?? 0;
  const judgedAcuteKg = (summary?.acuteOnSiteKg ?? 0) - acuteExemptKg;
  const acuteOver = summary?.acuteOnSiteCapKg != null && judgedAcuteKg > summary.acuteOnSiteCapKg;
  const acuteCaption =
    summary?.acuteOnSiteCapKg != null
      ? acuteCaptionText(judgedAcuteKg, summary.acuteOnSiteCapKg, acuteExemptKg)
      : undefined;
  const exemptCaption =
    summary?.onSiteCapKg != null && exemptKg > 0
      ? `${formatKg(exemptKg)} exempt — excluded from the on-site accumulation total`
      : undefined;

  return {
    caption: thresholdCaption(summary, totalOnSiteKg, exemptKg),
    acuteCaption,
    acuteOver,
    exemptCaption,
  };
}

function thresholdCaption(
  summary: OnSiteCapSummaryInput | undefined,
  totalOnSiteKg: number,
  exemptKg: number
): string {
  if (summary == null) {
    return "Accumulated on-site";
  }
  if (summary.onSiteCapKg == null) {
    return noThresholdCaption(summary);
  }
  if (exemptKg <= 0) {
    return `Accumulated on-site - ${formatKg(summary.onSiteCapKg)} threshold`;
  }
  return judgedThresholdCaption(summary, summary.onSiteCapKg, totalOnSiteKg, exemptKg);
}

/**
 * The acute sub-line's text: the judged mass against the 1 kg threshold, plus
 * a parenthetical noting any exempt acute mass — with the same "excluded from
 * the on-site accumulation total" wording the headline's `exemptCaption` uses,
 * not language suggesting the threshold itself moved.
 */
function acuteCaptionText(
  judgedAcuteKg: number,
  acuteOnSiteCapKg: number,
  exemptKg: number
): string {
  const base = `Acute: ${formatNumber(judgedAcuteKg)} kg of ${formatNumber(acuteOnSiteCapKg)} kg threshold`;
  if (exemptKg <= 0) {
    return base;
  }
  return `${base} (${formatNumber(exemptKg)} kg exempt — excluded from the on-site accumulation total)`;
}

// A resolved generator status with no threshold (an LQG, which is time-limited
// instead) names the status; no status at all means it still needs setting.
function noThresholdCaption(summary: OnSiteCapSummaryInput): string {
  if (summary.accumulationThresholdJudgedAgainst) {
    return `Accumulated on-site - no threshold (${summary.accumulationThresholdJudgedAgainst.abbreviation})`;
  }
  return "Accumulated on-site - generator status not set";
}

/**
 * The judged-math caption once exempt mass is present. A VSQG's threshold
 * governs non-acute mass alone (`onSiteCapAppliesTo === "NON_ACUTE"`), so its
 * judged number excludes acute mass entirely — mirroring how the card already
 * splits acute out into its own sub-caption against its own 1 kg threshold.
 * An SQG's threshold governs the combined total.
 */
function judgedThresholdCaption(
  summary: OnSiteCapSummaryInput,
  onSiteCapKg: number,
  totalOnSiteKg: number,
  exemptKg: number
): string {
  if (summary.onSiteCapAppliesTo === "NON_ACUTE") {
    const judgedNonAcuteKg = summary.nonAcuteOnSiteKg - summary.episodicExemptNonAcuteKg;
    return `${formatKg(judgedNonAcuteKg)} counted against ${formatKg(
      onSiteCapKg
    )} threshold — threshold applies to non-acute waste`;
  }
  const judgedKg = totalOnSiteKg - exemptKg;
  return `${formatKg(judgedKg)} counted against ${formatKg(onSiteCapKg)} threshold`;
}
